import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import React, { memo, useState } from 'react'

import { CheckRounded } from '@Assets/SVG'
import AddToCart2SVG from '@Assets/SVG/AddToCart2'
import { Chip } from '@Components/Chips/Chip/Chip'
import { ImageV3, ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import styles from '@Components/ProductList/ProductTiles/LoadMore/ProductTileV2/ProductTilesV2.module.scss'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { ProductTileProps } from '@Domains/Product/Tiles/types'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

export const ProductTile = memo(({
  alreadyAddedToCart,
  beforePrice,
  boosted = false,
  bottomChips,
  brandTitle,
  bundleCombinedValue,
  className,
  enableHover = true,
  hoverImage,
  id,
  imageWidth,
  isOutOfStock = false,
  loadingBuyButton,
  disableBuyButton = loadingBuyButton,
  onBuy,
  price,
  primaryImage,
  priority = true,
  sourceID,
  title,
  topChips,
  url,
  variantsColorCount,
}: ProductTileProps) => {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)

  const originalPrice = beforePrice || price
  const overwrittenPrice = beforePrice ? price : undefined
  const showBrand = !!brandTitle
  const showVariants = !!variantsColorCount && variantsColorCount > 1

  return (
    <Link
      analytics={{
        source_id: sourceID,
        dest_page_type: 'product',
        dest_page_type_id: id,
      }}
      className={clsx(
        'th-p',
        className,
        styles.ProductTileV2,
        priority && styles.priority,
        !!onBuy && styles.withActions,
        !!hoverImage && enableHover && styles.hasHover,
        boosted && 'th-b',
      )}
      href={url}
      id={id}
      onMouseOver={() => setLoaded(true)}
    >
      {isOutOfStock && (
        <div className={styles.outOfStock}>
          <p>{t('cms:OUT_OF_STOCK')}</p>
        </div>
      )}

      {!priority && primaryImage && (
        <ImageV4
          alt={title}
          className={clsx(styles.images, styles.image)}
          decoding="async"
          draggable={false}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          src={primaryImage}
          style={{
            objectFit: 'contain',
          }}
          title={title}
          width={imageWidth || (priority || enableHover) ? 400 : 180}
        />
      )}

      {priority && primaryImage && (
        <ImageV3
          alt={title}
          className={clsx(styles.images, styles.image)}
          decoding="async"
          draggable={false}
          fetchPriority="high"
          fill
          loading="eager"
          onLoad={() => setLoaded(true)}
          priority
          sizes="(max-width: 767px) 50vw, (max-width: 1199px) 33vw, (max-width: 1999px) 25vw, 20vw"
          src={primaryImage}
          style={{
            objectFit: 'contain',
          }}
          title={title}
        />
      )}

      {hoverImage && loaded && enableHover && (
        <ImageV4
          alt={`hover: ${title}`}
          className={clsx(styles.images, styles.imageHover)}
          decoding="async"
          draggable={false}
          loading="lazy"
          src={hoverImage}
          style={{
            objectFit: 'cover',
          }}
          title={`hover: ${title}`}
          width={imageWidth || enableHover ? 500 : 180}
        />
      )}

      <p className={styles.title}>{title}</p>

      {!isEmpty(topChips) && (
        <div className={styles.topChips}>
          {map(topChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}

      {!isEmpty(bottomChips) && (
        <div className={styles.bottomChips}>
          {map(bottomChips, chip => (
            <Chip key={`chip${'text' in chip ? chip.text : chip.translation}`} color={chip.color} icon={chip.icon}>
              {'text' in chip ? chip.text : t(...(chip.translation as unknown as any))}
            </Chip>
          ))}
        </div>
      )}

      <div className={styles.price}>
        <p className={clsx(overwrittenPrice && styles.strikeThrough)}>{originalPrice}</p>
        <p>{overwrittenPrice}</p>
      </div>

      {bundleCombinedValue && (
        <div className={styles.bundle}>
          {`${t('common:BUNDLE_COMBINED_VALUE')} - ${bundleCombinedValue}`}
        </div>
      )}

      {!!onBuy && (
        <div className={styles.actions}>
          <button
            className={clsx(styles.buyButton, alreadyAddedToCart && styles.success, sourceID)}
            disabled={disableBuyButton}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onBuy(id)
            }}
            type="button"
          >
            {' '}
            {loadingBuyButton && <Spinner className={styles.addToCartSpinner} />}
            {(!loadingBuyButton && !alreadyAddedToCart) && <AddToCart2SVG />}
            {(!loadingBuyButton && alreadyAddedToCart) && <CheckRounded className={styles.alreadyAddedIcon} />}
          </button>
        </div>
      )}

      <AddToWishList
        className={styles.wish}
        id={id}
        sourceID={sourceID}
      />

      {!!(showBrand || showVariants) && (
        <div className={styles.brandVariants}>
          {showVariants && (
            <span>
              <p className={styles.variants}>{`${variantsColorCount} ${t('products:COLOURS_LABEL')}`}</p>
            </span>
          )}
          {showBrand && <p className={styles.brand}>{brandTitle}</p>}
        </div>
      )}
    </Link>
  )
}, (prev, next) => (
  prev.className === next.className
  && prev.enableHover === next.enableHover
  && prev.loadingBuyButton === next.loadingBuyButton
  && prev.disableBuyButton === next.disableBuyButton
  && prev.isOutOfStock === next.isOutOfStock
  && prev.onBuy === next.onBuy
))
